import { useEffect, useMemo, type FunctionComponent } from "react";
import { Alert, DatePicker, Form, Input, InputNumber, Select, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { type ProductWithStats } from "@fitness-app/app-store";
import { DEFAULT_CURRENCY_OPTIONS, DEFAULT_PRODUCT_CURRENCY } from "@fitness-app/data-models";
import { DiscountDurationScope, DiscountStatus, DiscountType } from "@fitness-app/data-models/entities/Discount";
import { PriceStatus, ProductChargeType, type ProductPrice } from "@fitness-app/data-models/entities/Product";

import DiscountGatewayCoverage from "~/modules/Products/Discounts/DiscountGatewayCoverage";
import { usePaymentGatewayOptions } from "~/modules/Products/ProductForm/hooks/usePaymentGatewayOptions";
import { useAppSelector } from "~/store/initializeStore";
import { type DiscountFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<DiscountFormModel>;
  onSubmit: (formData: DiscountFormModel) => void;
  model?: DiscountFormModel | null;
  extraTakenCodes?: string[];
}

type Props = OwnProps;

type SelectOption = {
  label: string;
  value: string | number;
};

type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

const DEFAULT_GROUP_KEY = "__default__";
const DEFAULT_GROUP_LABEL = "Główne";

const currencyOptions = DEFAULT_CURRENCY_OPTIONS.map((currency) => ({
  value: currency,
  label: currency.toUpperCase(),
}));

const getPrices = (product: ProductWithStats): ProductPrice[] =>
  "prices" in product && Array.isArray(product.prices) ? product.prices : [];

const formatPrice = (price: ProductPrice): string =>
  `${(price.unit_amount ?? 0) / 100} ${price.currency.toUpperCase()}`;

const DiscountForm: FunctionComponent<Props> = ({ formController, onSubmit, model, extraTakenCodes }: Props) => {
  const { t } = useTranslation(["settings", "common", "products"]);
  const productsListData = useAppSelector((store) => store.products.productsListData);
  // Filtering inside the selector returned a fresh array on every call, which re-triggered
  // every effect depending on `productsList` and caused a setState render loop (max update depth).
  const productsList = useMemo(
    () => productsListData.filter((product) => product.chargeType !== ProductChargeType.FREE),
    [productsListData],
  );
  const discounts = useAppSelector((store) => store.discount.discounts);
  const productsSettings = useAppSelector((store) => store.productsSettings.data);
  const { stripeOptionsByKey } = usePaymentGatewayOptions();
  const hasNonStripeGatewayConnected = Boolean(
    productsSettings?.tpay || (productsSettings?.paymentGateways?.TPAY?.length ?? 0) > 0,
  );
  const stripeConnected = Boolean(productsSettings?.stripeConnectedAccountId || productsSettings?.isStripeConnected);
  const type = Form.useWatch("type", formController);
  const products = Form.useWatch("products", formController);
  const showTimeLimit = Form.useWatch("showTimeLimit", formController);
  const showQuantityLimit = Form.useWatch("showQuantityLimit", formController);
  const value = Form.useWatch("value", formController);
  const prices = Form.useWatch("prices", formController);
  const durationScope = Form.useWatch("durationScope", formController);

  const uniqueCodes = useMemo(() => {
    const localCodes = discounts.map((discount) => discount.code.toLowerCase());
    const remoteCodes = (extraTakenCodes ?? []).map((code) => code.toLowerCase());

    return Array.from(new Set([...localCodes, ...remoteCodes]));
  }, [discounts, extraTakenCodes]);

  useEffect(() => {
    if (extraTakenCodes && extraTakenCodes.length > 0) {
      void formController?.validateFields(["code"]).catch(() => undefined);
    }
  }, [extraTakenCodes, formController]);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue({
        ...model,
        code: model.code.toUpperCase(),
        showTimeLimit: Boolean(model.limitRange),
        showQuantityLimit: Boolean(model.quantityLimit),
        limitRange: model.limitRange
          ? {
              from: dayjs(model.limitRange.from),
              to: model.limitRange.to ? dayjs(model.limitRange.to) : null,
            }
          : null,
      });
    }
  }, [formController, model]);

  // Group products per company (Stripe account / spółka), mirroring the "Produkt"
  // filter in products?tab=customers. Products on the default account fall into a
  // single "Główne" group; products on a dedicated account are grouped by its name.
  const productOptions = useMemo<SelectOptionGroup[]>(() => {
    const groups: Record<string, SelectOptionGroup> = {
      [DEFAULT_GROUP_KEY]: { label: DEFAULT_GROUP_LABEL, options: [] },
    };

    for (const product of productsList) {
      const stripeAcc = product.customStripeAccount ? stripeOptionsByKey[product.customStripeAccount] : undefined;
      const key =
        product.customStripeAccount && stripeAcc && !stripeAcc.isDefault
          ? product.customStripeAccount
          : DEFAULT_GROUP_KEY;

      if (!groups[key]) {
        groups[key] = { label: stripeAcc?.name ?? key, options: [] };
      }
      groups[key].options.push({ label: product.name, value: product.id });
    }

    return Object.values(groups).filter((group) => group.options.length > 0);
  }, [productsList, stripeOptionsByKey]);

  const priceOptions = useMemo<SelectOption[]>(() => {
    if (!products?.length) {
      return [];
    }

    const productsChosen = productsList.filter((product) => products.includes(product.id));
    // Every active price is selectable regardless of `durationScope`: a "once"
    // discount is perfectly valid on a recurring/installment price — Stripe just
    // applies it to the first invoice (that is the "100% off the first
    // installment" scenario). Filtering recurring prices out here did not stop
    // the discount from reaching them, it only forced the owner to leave
    // `prices` empty, which makes the code apply to EVERY price of the product.
    const availablePrices = productsChosen
      .flatMap((product) => getPrices(product))
      .filter((price): price is ProductPrice => !!price?.active && price?.status === PriceStatus.Available);

    return availablePrices.map((price) => {
      const productDetails = productsChosen.find((product) =>
        getPrices(product).some((productPrice) => productPrice.id === price.id),
      );
      const paymentType = t(`products:paymentTypeOptions.${price?.type || "recurring"}`);

      return {
        label: `${productsChosen.length > 1 ? `${productDetails?.name} | ` : ""}${
          price?.customLabel ?? paymentType
        } - ${formatPrice(price)}`,
        value: `${price?.id}`,
      };
    });
  }, [products, productsList, t]);

  const amountExceedsPrice = useMemo(() => {
    if (!products?.length || !value || type !== DiscountType.Number) {
      return false;
    }

    const productsChosen = productsList.filter((product) => products.includes(product.id));
    const fullyDiscountedPrices = productsChosen
      .flatMap((product) => getPrices(product))
      .filter(
        (price): price is ProductPrice =>
          !!price?.active && price?.status === PriceStatus.Available && (price?.unit_amount ?? 0) <= value * 100,
      );

    return fullyDiscountedPrices.length > 0;
  }, [value, type, products, productsList]);

  useEffect(() => {
    if (type === DiscountType.Number) {
      formController?.setFieldsValue({
        currency: model?.currency ?? DEFAULT_PRODUCT_CURRENCY,
      });
    } else {
      formController?.setFieldsValue({
        currency: null,
      });
    }
  }, [type, formController]);

  return (
    <Form<DiscountFormModel>
      name="discount-form"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      layout="horizontal"
      labelWrap
      form={formController}
      initialValues={{
        id: model?.id ?? undefined,
        code: "",
        type: DiscountType.Percent,
        value: !hasNonStripeGatewayConnected ? 100 : 1,
        currency: DEFAULT_PRODUCT_CURRENCY,
        status: DiscountStatus.Active,
        active: true,
        showQuantityLimit: false,
        quantityLimit: 1,
        showTimeLimit: false,
        limitRange: {
          from: null,
          to: null,
        },
        prices: [],
        durationScope: DiscountDurationScope.Once,
      }}
      onFinish={onSubmit}
    >
      {type === DiscountType.Percent && value === 100 && prices?.length === 0 && (
        <div className="mb-5 flex flex-row flex-wrap">
          <Alert
            message={t(
              durationScope === DiscountDurationScope.Forever
                ? "settings:discount.form.fullDiscountWithNoPriceSelectedWarningForever"
                : "settings:discount.form.fullDiscountWithNoPriceSelectedWarning",
            )}
            type="warning"
            className="flex-1"
          />
        </div>
      )}
      <Form.Item
        name="code"
        label={t("settings:discount.form.code")}
        rules={[
          { required: true, message: t<string>("common:validationErrors.fieldIsRequired") },
          {
            pattern: /^[a-zA-Z0-9\-_]+$/,
            message: t<string>("settings:discount.form.validationErrors.codeInvalidFormat"),
          },
          ({ getFieldValue }) => ({
            validator() {
              const codeValue = (getFieldValue("code") as string | undefined)?.toLowerCase();
              const hasId = Boolean(getFieldValue("id"));
              // `uniqueCodes` already merges `extraTakenCodes` (see useMemo above),
              // so a single check covers both local + remote-taken codes.
              if (codeValue && uniqueCodes.includes(codeValue) && !hasId) {
                return Promise.reject(t<string>("settings:discount.form.validationErrors.codeAlreadyTaken"));
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <Input
          placeholder={t<string>("settings:discount.form.codePlaceholder")}
          style={{ maxWidth: 700 }}
          disabled={Boolean(model?.id)}
          onChange={(e) => formController?.setFieldsValue({ code: e.currentTarget.value.toUpperCase() })}
        />
      </Form.Item>
      <Form.Item
        name="type"
        label={t("settings:discount.form.type")}
        rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
      >
        <Select notFoundContent={false}>
          <Select.Option value={DiscountType.Percent} key={DiscountType.Percent}>
            {t(`settings:discount.form.types.${DiscountType.Percent}`)}
          </Select.Option>
          {!hasNonStripeGatewayConnected ? null : (
            <Select.Option value={DiscountType.Number} key={DiscountType.Number}>
              {t(`settings:discount.form.types.${DiscountType.Number}`)}
            </Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item
        label={type === DiscountType.Percent ? t("settings:discount.form.value") : t("settings:discount.form.valueTax")}
        style={{ marginBottom: 0 }}
      >
        <Form.Item name="value" style={{ display: "inline-block" }}>
          <InputNumber
            placeholder={t<string>("settings:discount.form.valuePlaceholder")}
            min={!hasNonStripeGatewayConnected ? 100 : 1}
            max={type === DiscountType.Percent ? 100 : Number.MAX_VALUE}
            style={{ maxWidth: 700 }}
          />
        </Form.Item>

        {type === DiscountType.Number && (
          <Form.Item
            name="currency"
            style={{ display: "inline-block", marginLeft: 8, minWidth: 70 }}
            rules={[
              {
                required: type === DiscountType.Number,
                message: t<string>("common:validationErrors.fieldIsRequired"),
              },
            ]}
          >
            <Select options={currencyOptions} defaultValue={DEFAULT_PRODUCT_CURRENCY} />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item label={t("settings:discount.form.showQuantityLimit")} name="showQuantityLimit" valuePropName="checked">
        <Switch />
      </Form.Item>
      {showQuantityLimit && (
        <Form.Item
          label={t("settings:discount.form.limitQuantity")}
          name="quantityLimit"
          rules={[
            {
              required: showQuantityLimit,
              type: "number",
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <InputNumber min={0} />
        </Form.Item>
      )}
      <Form.Item label={t("settings:discount.form.showTimeLimit")} name="showTimeLimit" valuePropName="checked">
        <Switch />
      </Form.Item>
      {showTimeLimit && (
        <>
          <Form.Item
            name={["limitRange", "from"]}
            label={t("settings:discount.form.limitTimeFrom")}
            tooltip={t<string>("settings:discount.form.limitTimeFromHint")}
            rules={[
              {
                validator: (_, fieldValue) =>
                  !fieldValue || fieldValue.valueOf() >= Date.now()
                    ? Promise.resolve()
                    : Promise.reject(new Error(t<string>("common:validationErrors.cannotBeEarlierThanNow"))),
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD | HH:mm"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </Form.Item>

          <Form.Item
            name={["limitRange", "to"]}
            label={t("settings:discount.form.limitTimeTo")}
            rules={[
              {
                validator: (_, fieldValue) =>
                  !fieldValue || fieldValue.valueOf() >= Date.now()
                    ? Promise.resolve()
                    : Promise.reject(new Error(t<string>("common:validationErrors.cannotBeEarlierThanNow"))),
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD | HH:mm"
              disabledDate={(current) => {
                const from = formController?.getFieldValue(["limitRange", "from"]) as string;
                const lowerBound = from ? dayjs(from) : dayjs().startOf("day");
                return Boolean(current && current.valueOf() < lowerBound.valueOf());
              }}
            />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="products"
        label={t("settings:discount.form.products")}
        rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
      >
        <Select mode="multiple" showSearch optionFilterProp="label" options={productOptions} />
      </Form.Item>
      {amountExceedsPrice && (
        <div className="mb-5 flex flex-row flex-wrap">
          <div className="basis-1/3">&nbsp;</div>
          <Alert message={t("settings:discount.form.fullDiscountPriceWarning")} type="warning" className="flex-1" />
        </div>
      )}
      {products && products?.length > 0 && (
        <Form.Item name="prices" label={t("settings:discount.form.prices")}>
          <Select mode="multiple" showSearch optionFilterProp="label" options={priceOptions} />
        </Form.Item>
      )}
      {products && products.length > 0 && (
        <Form.Item label={t("settings:discount.form.gatewayCoverage")}>
          <DiscountGatewayCoverage productIds={products} products={productsList} />
        </Form.Item>
      )}
      {stripeConnected && (
        <Form.Item
          name="durationScope"
          label={t("settings:discount.form.durationScope.label")}
          tooltip={t("settings:discount.form.durationScope.description")}
        >
          <Select>
            <Select.Option value={DiscountDurationScope.Once} key={DiscountDurationScope.Once}>
              {t(`settings:discount.form.durationScope.options.${DiscountDurationScope.Once}`)}
            </Select.Option>
            <Select.Option value={DiscountDurationScope.Forever} key={DiscountDurationScope.Forever}>
              {t(`settings:discount.form.durationScope.options.${DiscountDurationScope.Forever}`)}
            </Select.Option>
          </Select>
        </Form.Item>
      )}
      <Form.Item
        name="active"
        label={t("settings:discount.form.active")}
        rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    </Form>
  );
};

export default DiscountForm;
